import { create } from 'zustand'
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore'
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth'
import { db, secondaryAuth } from '../config/firebase'
import { Admin, AdminRole } from '../models/User'

interface AdminState {
  admins: Admin[]
  loading: boolean
  error: string | null
  fetchAdmins: () => Promise<void>
  createAdmin: (data: {
    email: string
    password: string
    displayName: string
    role: AdminRole
  }) => Promise<void>
  updateAdmin: (id: string, data: Partial<Admin>) => Promise<void>
  deleteAdmin: (id: string) => Promise<void>
}

export const useAdminStore = create<AdminState>((set, get) => ({
  admins: [],
  loading: false,
  error: null,

  fetchAdmins: async () => {
    set({ loading: true, error: null })
    try {
      const q = query(collection(db, 'admins'), orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)
      const admins: Admin[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        email: doc.data().email,
        displayName: doc.data().displayName,
        role: doc.data().role,
        createdAt: doc.data().createdAt?.toDate(),
        isActive: doc.data().isActive,
      }))
      set({ admins, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '載入失敗',
      })
    }
  },

  createAdmin: async (data) => {
    set({ loading: true, error: null })
    try {
      // 用 secondary auth 建立 Firebase Auth 用戶（不會登出當前 admin）
      let uid: string
      try {
        const credential = await createUserWithEmailAndPassword(
          secondaryAuth,
          data.email,
          data.password
        )
        uid = credential.user.uid
      } catch (authError: any) {
        if (authError?.code === 'auth/email-already-in-use') {
          // Auth 帳號已存在（可能之前建立失敗殘留）→ 嘗試登入取得 UID
          const credential = await signInWithEmailAndPassword(
            secondaryAuth,
            data.email,
            data.password
          )
          uid = credential.user.uid
        } else {
          throw authError
        }
      }
      // 立即登出 secondary auth，避免殘留 session
      await signOut(secondaryAuth)

      // 建立 admins collection 文件
      await setDoc(doc(db, 'admins', uid), {
        email: data.email,
        displayName: data.displayName,
        role: data.role,
        createdAt: new Date(),
        isActive: true,
      })

      // 同時建立 users collection 文件，讓 admin 可以直接登入 App
      await setDoc(doc(db, 'users', uid), {
        email: data.email,
        displayName: data.displayName,
        gender: 'other',
        birthDate: new Date(2000, 0, 1),
        education: 'bachelor',
        grade: 1,
        courses: [],
        registeredAt: new Date(),
        registeredBy: 'admin',
        loginCount: 0,
        lastLoginAt: null,
        fcmToken: null,
        totalScreenTime: 0,
      })

      // 重新載入列表
      await get().fetchAdmins()
      set({ loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '建立失敗',
      })
      throw error
    }
  },

  updateAdmin: async (id, data) => {
    set({ loading: true, error: null })
    try {
      await updateDoc(doc(db, 'admins', id), data)
      await get().fetchAdmins()
      set({ loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '更新失敗',
      })
      throw error
    }
  },

  deleteAdmin: async (id) => {
    set({ loading: true, error: null })
    try {
      await deleteDoc(doc(db, 'admins', id))
      await get().fetchAdmins()
      set({ loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '刪除失敗',
      })
      throw error
    }
  },
}))
